import React, { useState } from "react";
import {
  Plus,
  Grid,
  BarChart3,
  PieChart,
  Activity,
  Calendar,
  Settings,
  TrendingUp,
  TrendingDown,
  GitBranch,
  Database,
  Search,
  Filter,
  Layout,
  Table2,
  ChartLine,
  Timer,
  Percent,
  Hash,
  Hourglass,
  X,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../components/ui/dialog";
import { searchComponents } from "./componentSearch";

export const AVAILABLE_COMPONENTS = [
  // Active components (visible in All and their categories)
  {
    id: "abc-xyz-analysis",
    name: "ABC/XYZ Matrix",
    description:
      "Ranks process variants or object groups by importance (A/B/C) and stability over time (X/Y/Z) in a 3×3 matrix",
    icon: Grid,
    category: "New",
  },
  {
    id: "attribute-table",
    name: "Attribute Table",
    description: "Display object attribute values with frequency counts",
    icon: Table2,
    category: "Analytics",
  },
  {
    id: "object-table",
    name: "Object Table",
    description: "Configurable object list with attribute columns, time scope presets, activity filters, and numeric aggregations",
    icon: Table2,
    category: "Analytics",
  },
  {
    id: "aggregation-kpi",
    name: "Aggregation KPI",
    description: "Single aggregated numeric KPI (e.g. sum of price) for a selected object type and configurable time scope",
    icon: TrendingUp,
    category: "Analytics",
  },
  {
    id: "attribute-sum-bar-chart",
    name: "Attribute Sum Bar Chart",
    description: "Sum numeric event/object attributes over time by granularity",
    icon: BarChart3,
    category: "Analytics",
  },
  {
    id: "visual-conformance-checker",
    name: "Conformance Checking",
    description: "Side-by-side graph comparison of reference and observed process variants. Activities are color-coded by conformance status: match, missing, or extra.",
    icon: GitBranch,
    category: "New",
  },
  {
    id: "trace-pivot-table",
    name: "Pivot Table",
    description: "Interactive pivot table over object-event traces — drag fields to group, aggregate, and filter by activity, object type, duration, and more",
    icon: Table2,
    category: "New",
  },
  {
    id: "gantt-chart",
    name: "Gantt Chart",
    description: "Timeline view of object lifecycles — visualize start/end activities per object as task bars grouped by a summary attribute",
    icon: Calendar,
    category: "New",
  },
  {
    id: "attribute-sum-line-chart",
    name: "Attribute Sum Line Chart",
    description: "Line chart of numeric event/object attribute sums over time with cumulative mode and comparison periods",
    icon: ChartLine,
    category: "New",
  },
  {
    id: "overdue-value-timeline",
    name: "Overdue Value Timeline",
    description: "Area chart showing the aggregated value of overdue, not-yet-completed objects per calendar day — wertgewichteter Verzögerungsbestand",
    icon: TrendingDown,
    category: "KPIs",
  },
  {
    id: "liefertermintreue-timeline",
    name: "Liefertermintreue über Zeit",
    description: "Zeitlicher Verlauf der Liefertermintreue (%) — tägliche Quote plus geglättete Rolling-Average-Linie, mit Ampel-Schwellenwerten",
    icon: Percent,
    category: "KPIs",
  },
  {
    id: "absolute-delay-timeline",
    name: "Absoluter Verzug über Zeit",
    description: "Zeitlicher Verlauf des absoluten Verzugs in Tagen (Min/Median/Durchschnitt/Max), inkl. noch offener überfälliger Aufträge",
    icon: TrendingDown,
    category: "KPIs",
  },
  {
    id: "durchlaufzeit-timeline",
    name: "Durchlaufzeit über Zeit",
    description: "Zeitlicher Verlauf der Durchlaufzeit zwischen zwei Ereignissen in Tagen (Min/Median/Durchschnitt/Max), inkl. noch laufender Aufträge",
    icon: Hourglass,
    category: "KPIs",
  },
  {
    id: "transition-time-line-chart",
    name: "Transition Time Line Chart",
    description: "Median transition time per time bucket — keeps only transitions strictly below the configured threshold (values at/above are treated as downtime and excluded)",
    icon: ChartLine,
    category: "New",
  },
  {
    id: "date-deviation-kpi-custom",
    name: "Date Deviation KPI",
    description: "Measures deviation from expected/planned dates",
    icon: Calendar,
    category: "KPIs",
  },
  {
    id: "liefertermintreue-kpi",
    name: "Delivery Date Compliance KPI",
    description: "Percentage of objects completed on time (actual date ≤ reference date), configurable per object type",
    icon: Percent,
    category: "KPIs",
  },
  {
    id: "efficiency-kpi",
    name: "Efficiency KPI",
    description: "Configurable percentage KPI from a numerator/denominator formula over object attributes (e.g. material or train efficiency), with optional traffic-light coloring",
    icon: Percent,
    category: "KPIs",
  },
  {
    id: "object-count-kpi",
    name: "Object Count KPI",
    description: "Single object count for a selected object type, scoped by global/local filters, with optional traffic-light thresholds and previous-period comparison",
    icon: Hash,
    category: "KPIs",
  },
  {
    id: "object-conversion-kpi",
    name: "Object Conversion KPI",
    description: "Percentage of objects of a type that satisfy a numerator activity condition (AND/OR, e.g. 'Angebot → Auftrag') relative to a configurable denominator condition, with optional traffic-light coloring",
    icon: Percent,
    category: "KPIs",
  },
  {
    id: "lead-time-histogram",
    name: "Lead Time Histogram",
    description: "Lead time distribution by object type",
    icon: BarChart3,
    category: "Analytics",
  },
  {
    id: "lead-time-statistics",
    name: "Lead Time Statistics",
    description: "Shows the max, min, and median lead time and the total count for the selected object type.",
    icon: Timer,
    category: "Analytics",
  },
  {
    id: "resource-availability-kpi",
    name: "Resource Utilization KPI",
    description: "Utilization percentage for selected resources over day/week/month/year",
    icon: Percent,
    category: "KPIs",
  },
  {
    id: "resource-utilization-chart",
    name: "Resource Utilization Chart",
    description: "Bar chart showing utilization % per time bucket over the selected timerange",
    icon: BarChart3,
    category: "KPIs",
  },
  {
    id: "resource-status-monitor",
    name: "Resource Status Monitor",
    description:
      "Binary availability status bar per resource: configurable-width time slots per day (green/red/grey)",
    icon: BarChart3,
    category: "New",
  },
  {
    id: "root-cause-analysis",
    name: "Root Cause Analysis",
    description: "Analyze root causes of process deviations",
    icon: Search,
    category: "Process",
  },
  {
    id: "total-downtime-kpi",
    name: "Total Downtime KPI",
    description: "Analyze machine/resource downtime within shift schedules",
    icon: BarChart3,
    category: "KPIs",
  },
  {
    id: "automation-potential-kpi",
    name: "Automation Potential",
    description: "Quantifies manual and monetary automation potential of an activity based on its actual frequency and configured effort",
    icon: TrendingUp,
    category: "KPIs",
  },
  {
    id: "variant-explorer-extended",
    name: "Variant Explorer Extended",
    description: "Variant analysis with visual activity sequences",
    icon: GitBranch,
    category: "New",
  },

  // Executive — simplified KPI tiles for management overview
  {
    id: "executive-resource-utilization-tile",
    name: "Resource Utilization (Executive)",
    description: "Overall resource utilization % with traffic-light status. Click navigates to a target tab.",
    icon: Percent,
    category: "Executive",
  },

  {
    id: "biaxial-scatter-plot",
    name: "Biaxial Scatter Plot",
    description:
      "Scatter diagram comparing two freely configurable values (object attribute or activity timestamp) per object. Points on the 45° diagonal mean equal values on both axes.",
    icon: Activity,
    category: "New",
  },

  // Archive — not shown in All or other category filters
  {
    id: "resource-calendar",
    name: "Resource Calendar",
    description:
      "Interactive calendar demo using FullCalendar (MIT): create/move/resize/delete blocks",
    icon: Calendar,
    category: "New",
  },
  {
    id: "activity-counts-tile",
    name: "Activity Counts Tile",
    description: "Number of events grouped by activity/event type",
    icon: BarChart3,
    category: "Archive",
  },
  {
    id: "activity-frequency-ratio-kpi",
    name: "Activity Frequency Ratio KPI",
    description:
      "Ratio of activity frequencies to total objects of selected type",
    icon: TrendingUp,
    category: "Archive",
  },
  {
    id: "activity-reoccurrence-kpi",
    name: "Activity Reoccurrence KPI",
    description: "Analysis of activity reoccurrence patterns and timing",
    icon: Activity,
    category: "Archive",
  },
  {
    id: "avg-events-per-object-type",
    name: "Avg Events per Object Type",
    description: "Average events per object, grouped by object type",
    icon: TrendingUp,
    category: "Archive",
  },
  {
    id: "count-event-types",
    name: "Count Event Types",
    description: "Number of distinct event types",
    icon: TrendingUp,
    category: "Archive",
  },
  {
    id: "count-object-types",
    name: "Count Object Types",
    description: "Number of distinct object types",
    icon: TrendingUp,
    category: "Archive",
  },
  {
    id: "count-time-span",
    name: "Count Time Span",
    description: "Overall time span covered by the dataset",
    icon: Calendar,
    category: "Archive",
  },
  {
    id: "count-total-events",
    name: "Count Total Events",
    description: "Total number of events in the dataset",
    icon: TrendingUp,
    category: "Archive",
  },
  {
    id: "count-total-objects",
    name: "Count Total Objects",
    description: "Total number of objects in the dataset",
    icon: TrendingUp,
    category: "Archive",
  },
  {
    id: "edge-duration",
    name: "Edge Duration",
    description: "Duration statistics for transitions between activities",
    icon: TrendingUp,
    category: "Archive",
  },
  {
    id: "edge-duration-histogram",
    name: "Edge Duration Histogram",
    description:
      "Frequency distribution of edge durations (duration on X, count on Y)",
    icon: BarChart3,
    category: "Archive",
  },
  {
    id: "edge-frequency",
    name: "Edge Frequency",
    description: "Count of events between process nodes",
    icon: GitBranch,
    category: "Archive",
  },
  {
    id: "event-counts-per-object-type",
    name: "Event Counts per Object Type",
    description: "Total events per object type",
    icon: BarChart3,
    category: "Archive",
  },
  {
    id: "event-intensity-per-object-type",
    name: "Event Intensity per Object Type",
    description: "Average number of events per object by object type",
    icon: TrendingUp,
    category: "Archive",
  },
  {
    id: "event-types-per-object-type",
    name: "Event Types per Object Type",
    description: "Top object types by number of distinct activities",
    icon: Activity,
    category: "Archive",
  },
  {
    id: "lead-time-dashboard",
    name: "Lead Time Dashboard",
    description: "Comprehensive lead time analysis dashboard",
    icon: Layout,
    category: "Archive",
  },
  {
    id: "missing-activities",
    name: "Missing Activities",
    description: "Count and rate of events with missing/invalid activity label",
    icon: Activity,
    category: "Archive",
  },
  {
    id: "missing-event-object-ref",
    name: "Missing Event Object Ref",
    description: "Events not referencing any related objects",
    icon: GitBranch,
    category: "Archive",
  },
  {
    id: "missing-object-types",
    name: "Missing Object Types",
    description: "Number of objects with missing or invalid type",
    icon: Settings,
    category: "Archive",
  },
  {
    id: "missing-timestamps",
    name: "Missing Timestamps",
    description: "Count and rate of events without a valid timestamp",
    icon: Calendar,
    category: "Archive",
  },
  {
    id: "object-counts-by-selected-activities",
    name: "Object Counts by Selected Activities",
    description: "Count objects meeting activity-based criteria (AND/OR)",
    icon: Database,
    category: "Archive",
  },
  {
    id: "object-details",
    name: "Object Details",
    description: "Detailed information about process objects",
    icon: Database,
    category: "Archive",
  },
  {
    id: "object-type-combinations",
    name: "Object Type Combinations",
    description: "Combinations of object types appearing together in events",
    icon: Grid,
    category: "Archive",
  },
  {
    id: "object-type-frequencies",
    name: "Object Type Frequencies",
    description: "Frequency of objects per object type",
    icon: PieChart,
    category: "Archive",
  },
  {
    id: "ocdfg-viewer",
    name: "OCDFG Viewer",
    description: "Interactive process flow visualization using Cytoscape",
    icon: Grid,
    category: "Archive",
  },
  {
    id: "orphan-objects",
    name: "Orphan Objects",
    description: "Objects that are not referenced by any events",
    icon: Database,
    category: "Archive",
  },
  {
    id: "pre-activity-selector",
    name: "Pre-Activity Selector",
    description: "Build and filter by custom activity sequences",
    icon: Filter,
    category: "Archive",
  },
  {
    id: "react-flow-graph",
    name: "React Flow Graph",
    description: "Alternative process graph visualization using React Flow",
    icon: Grid,
    category: "Archive",
  },
  {
    id: "time-span-tile",
    name: "Time Span Tile",
    description: "Display the time span of the dataset",
    icon: Calendar,
    category: "Archive",
  },
  {
    id: "timeline-diagram",
    name: "Timeline Diagram",
    description: "Time-based performance metrics visualization",
    icon: Activity,
    category: "Archive",
  },
  {
    id: "variant-explorer",
    name: "Variant Explorer",
    description: "Process variant analysis and selection",
    icon: GitBranch,
    category: "Archive",
  },
  {
    id: "weekday-activity-count",
    name: "Weekday Activity Count",
    description: "Activity distribution across days of the week",
    icon: Calendar,
    category: "Archive",
  },
];

const ComponentLibrary = ({
  onComponentAdd,
  className = "",
  disableFixedPositioning = false,
  allowedComponentIds = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  const activeComponents = AVAILABLE_COMPONENTS.filter(
    (comp) => comp.category !== "Archive"
      && (allowedComponentIds == null || allowedComponentIds.has(comp.id)),
  );

  const categories = [
    "All",
    ...new Set(activeComponents.map((comp) => comp.category)),
    "Archive",
  ];

  const categoryComponents =
    selectedCategory === "All"
      ? activeComponents
      : AVAILABLE_COMPONENTS.filter(
          (comp) => comp.category === selectedCategory
            && (allowedComponentIds == null || allowedComponentIds.has(comp.id)),
        );

  // Search always applies within the currently selected category, and
  // leaves ordering untouched while the search box is empty.
  const filteredComponents = searchComponents(categoryComponents, searchQuery);
  const trimmedSearchQuery = searchQuery.trim();

  // The modal's open/closed state is the source of truth for the search
  // box's lifecycle: every transition clears it, so reopening the modal
  // never shows stale search text from a previous session.
  const handleOpenChange = (nextOpen) => {
    setIsOpen(nextOpen);
    setSearchQuery("");
  };

  const handleComponentAdd = (component) => {
    if (onComponentAdd) {
      onComponentAdd(component);
    }
    handleOpenChange(false);
  };

  return (
    <>
      <div
        className={
          disableFixedPositioning
            ? className
            : `fixed top-4 left-40 z-50 ${className}`
        }
      >
        <button
          onClick={() => handleOpenChange(true)}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-md hover:shadow-lg transition-shadow"
        >
          <Plus className="w-5 h-5" />
          <span>Components</span>
        </button>
      </div>

      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-4xl p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-gray-200 flex-shrink-0">
            <DialogTitle>Add Component</DialogTitle>
          </DialogHeader>

          {/* Search + Category Filter */}
          <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search components..."
                aria-label="Search components"
                className="w-full pl-9 pr-9 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  aria-label="Clear search"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-3 py-1 text-sm rounded-full transition-colors ${
                    selectedCategory === category
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 min-h-0">
            {filteredComponents.length === 0 ? (
              <div className="text-center text-sm text-gray-500 py-12">
                No components found for &quot;{trimmedSearchQuery}&quot;.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredComponents.map((component) => {
                  const IconComponent = component.icon;
                  return (
                    <div
                      key={component.id}
                      className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 hover:shadow-sm transition-all cursor-pointer"
                      onClick={() => handleComponentAdd(component)}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 p-2 bg-blue-50 rounded-lg">
                          <IconComponent className="w-6 h-6 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-gray-900 text-sm leading-5">
                            {component.name}
                          </h3>
                          <p className="text-xs text-gray-500 mt-1 leading-4">
                            {component.description}
                          </p>
                          <div className="mt-2">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              {component.category}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <DialogFooter className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0 sm:justify-between">
            <span className="text-sm text-gray-600 self-center">
              Click on a component to add it to your dashboard
            </span>
            <button
              onClick={() => handleOpenChange(false)}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ComponentLibrary;
